import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';

const PRICE_KEY_PREFIX = 'sgi-price-v1:';
const GENERAL_PRICE_CLIENT_ID = '__general_price_list__';
const MANUAL_PRICE_VALUE = '__manual_price__';
const emptyItem = () => ({
  priceEntryKey: '',
  manualName: '',
  manualUnit: 'KG',
  manualPrice: '',
  expectedBoxes: '',
  expectedWeight: '',
  expectedQuantity: '',
});

const decodePriceEntry = (row) => {
  const encodedName = row?.productName || '';
  if (encodedName.startsWith(PRICE_KEY_PREFIX)) {
    try {
      return JSON.parse(decodeURIComponent(encodedName.slice(PRICE_KEY_PREFIX.length)));
    } catch {
      return null;
    }
  }
  return encodedName ? { section: 'bulk', product: encodedName } : null;
};

const describePriceEntry = (item) => [item.product, item.brand, item.variant].filter(Boolean).join(' · ');
const toNumberOrNull = (value) => value === '' || value === null || value === undefined ? null : Number(value);

const CreateOrderModal = ({ isOpen, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [clientId, setClientId] = useState('');
  const [manualClientName, setManualClientName] = useState('');
  const [manualClient, setManualClient] = useState(false);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedClient = useMemo(() => clients.find((client) => String(client.id || client._id) === String(clientId)), [clients, clientId]);
  const priceListClientId = manualClient ? GENERAL_PRICE_CLIENT_ID : clientId;
  const inputClass = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white';

  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    const loadOptions = async () => {
      setLoadingData(true);
      setError('');
      try {
        const clientResponse = await api.get('/clients');
        if (!active) return;
        setClients(Array.isArray(clientResponse.data) ? clientResponse.data : []);
      } catch (loadError) {
        console.error('Error loading order options', loadError);
        if (active) setError(t('orderOptionsLoadError'));
      } finally {
        if (active) setLoadingData(false);
      }
    };
    loadOptions();
    return () => { active = false; };
  }, [isOpen, t]);

  useEffect(() => {
    if (!isOpen || !priceListClientId) {
      setPriceItems([]);
      return undefined;
    }
    let active = true;
    const loadPrices = async () => {
      setLoadingPrices(true);
      setPriceItems([]);
      try {
        const response = await api.get(`/prices/client/${encodeURIComponent(priceListClientId)}`);
        if (!active) return;
        const available = (Array.isArray(response.data) ? response.data : [])
          .filter((row) => !row.catalogHidden && row.price !== null && row.price !== undefined && Number.isFinite(Number(row.price)))
          .map((row) => {
            const decoded = decodePriceEntry(row);
            if (!decoded?.product) return null;
            const section = decoded.section || 'bulk';
            return {
              entryKey: row.productName,
              price: Number(row.price),
              section,
              product: decoded.product,
              brand: decoded.brand || '',
              variant: decoded.variant || '',
              unit: section === 'piece' ? 'PIECE' : 'KG',
              label: describePriceEntry(decoded),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, 'es'));
        setPriceItems(available);
      } catch (loadError) {
        console.error('Error loading customer prices', loadError);
        if (active) setError(t('orderPricesLoadError'));
      } finally {
        if (active) setLoadingPrices(false);
      }
    };
    loadPrices();
    return () => { active = false; };
  }, [isOpen, priceListClientId, t]);

  const updateItem = (index, field, value) => {
    setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));
  };
  const addItem = () => setItems((current) => [...current, emptyItem()]);
  const removeItem = (index) => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));

  const handleClientChange = (value) => {
    setManualClient(value === MANUAL_PRICE_VALUE);
    setClientId(value === MANUAL_PRICE_VALUE ? '' : value);
    setManualClientName('');
    setItems([emptyItem()]);
    setError('');
  };

  const getItemPriceData = (item) => {
    const selectedPrice = priceItems.find((entry) => entry.entryKey === item.priceEntryKey);
    const manual = item.priceEntryKey === MANUAL_PRICE_VALUE;
    const unit = manual ? item.manualUnit : selectedPrice?.unit;
    const unitPrice = manual ? toNumberOrNull(item.manualPrice) : selectedPrice?.price;
    const quantity = unit === 'PIECE' ? toNumberOrNull(item.expectedQuantity) : toNumberOrNull(item.expectedWeight);
    return { selectedPrice, manual, unit, unitPrice, quantity };
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    const clientName = manualClient ? manualClientName.trim() : selectedClient?.name;
    if (!clientName) {
      setError(t('selectOrderCustomerError'));
      return;
    }

    const normalizedItems = items.map((item) => {
      const { selectedPrice, manual, unit, unitPrice, quantity } = getItemPriceData(item);
      const productName = manual ? item.manualName.trim() : selectedPrice?.label;
      const hasPrice = Number.isFinite(unitPrice) && unitPrice >= 0;
      const validQuantity = Number.isFinite(quantity) && quantity > 0;
      const expectedBoxes = unit === 'KG' ? toNumberOrNull(item.expectedBoxes) : null;
      return {
        productTemplateId: '',
        productName,
        priceEntryKey: manual ? null : selectedPrice?.entryKey,
        priceSection: manual ? 'manual' : selectedPrice?.section,
        priceUnit: unit,
        priceBrand: manual ? null : selectedPrice?.brand || null,
        priceVariant: manual ? null : selectedPrice?.variant || null,
        unitPrice: hasPrice ? unitPrice : null,
        estimatedTotal: hasPrice && validQuantity ? Number((unitPrice * quantity).toFixed(2)) : null,
        expectedBoxes,
        expectedWeight: unit === 'KG' ? toNumberOrNull(item.expectedWeight) : null,
        expectedQuantity: unit === 'PIECE' ? toNumberOrNull(item.expectedQuantity) : null,
        deliveredBoxes: 0,
        deliveredWeight: 0,
        deliveredQuantity: 0,
        _valid: Boolean(productName) && hasPrice && (unit === 'PIECE'
          ? Number.isInteger(quantity) && validQuantity
          : (validQuantity || (Number.isInteger(expectedBoxes) && expectedBoxes > 0))),
      };
    });

    if (!normalizedItems.length || normalizedItems.some((item) => !item._valid)) {
      setError(t('orderLineValidationError'));
      return;
    }
    const payloadItems = normalizedItems.map(({ _valid, ...item }) => item);
    setSaving(true);
    try {
      const now = new Date();
      const orderNumber = `PED-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
      await api.post('/api/orders', {
        clientId: manualClient ? null : clientId,
        clientName,
        orderNumber,
        notes: notes.trim(),
        items: payloadItems,
        productExitIds: [],
      });
      onSaved?.();
      onClose();
    } catch (saveError) {
      console.error('Error creating order', saveError);
      setError(t('orderSaveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl dark:bg-gray-900 md:p-7">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">{t('orders')}</p>
            <h2 className="text-2xl font-extrabold">{t('addOrder')}</h2>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label={t('cancel')}><X /></button>
        </div>

        <form onSubmit={handleSave} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-semibold">{t('client')}</label>
              <select className={inputClass} value={manualClient ? MANUAL_PRICE_VALUE : clientId} onChange={(event) => handleClientChange(event.target.value)} disabled={loadingData || saving} required>
                <option value="">{t('selectClient')}</option>
                {clients.map((client) => <option key={client.id || client._id} value={client.id || client._id}>{client.name}</option>)}
                <option value={MANUAL_PRICE_VALUE}>{t('newUnregisteredCustomer')}</option>
              </select>
            </div>
            {manualClient && <div>
              <label className="mb-1 block text-sm font-semibold">{t('customerName')}</label>
              <input className={inputClass} value={manualClientName} onChange={(event) => setManualClientName(event.target.value)} placeholder={t('customerNamePlaceholder')} maxLength={120} required disabled={saving} />
            </div>}
          </div>

          <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900 dark:bg-blue-950/40 dark:text-blue-100">
            {manualClient ? t('generalPricesForNewCustomer') : t('customerPriceSnapshotHelp')}
            {loadingPrices && <span className="ml-2">{t('loading')}</span>}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="font-bold">{t('orderProducts')}</h3>
              <button type="button" onClick={addItem} className="inline-flex items-center gap-1 rounded-lg border border-current px-3 py-1.5 text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-800"><Plus size={16} />{t('addProduct')}</button>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => {
                const manual = item.priceEntryKey === MANUAL_PRICE_VALUE;
                const selectedPrice = priceItems.find((entry) => entry.entryKey === item.priceEntryKey);
                const unit = manual ? item.manualUnit : selectedPrice?.unit;
                const unitPrice = manual ? toNumberOrNull(item.manualPrice) : selectedPrice?.price;
                const estimatedQty = unit === 'PIECE' ? toNumberOrNull(item.expectedQuantity) : toNumberOrNull(item.expectedWeight);
                const estimatedTotal = Number.isFinite(unitPrice) && estimatedQty > 0 ? unitPrice * estimatedQty : null;
                return (
                  <div key={index} className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{t('orderLine')} {index + 1}</span>
                      <button type="button" onClick={() => removeItem(index)} disabled={items.length === 1 || saving} className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-900/30" aria-label={t('removeProduct')}><Trash2 size={17} /></button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-semibold">{t('orderPriceProduct')}</label>
                        <select className={inputClass} value={item.priceEntryKey} onChange={(event) => updateItem(index, 'priceEntryKey', event.target.value)} disabled={loadingPrices || saving} required>
                          <option value="">{loadingPrices ? t('loading') : t('selectPriceProduct')}</option>
                          {priceItems.map((entry) => <option key={entry.entryKey} value={entry.entryKey}>{entry.label} — {t('currencyPrefix')}{entry.price.toFixed(2)} / {entry.unit === 'KG' ? 'KG' : t('pieceUnit')}</option>)}
                          <option value={MANUAL_PRICE_VALUE}>{t('addManualProduct')}</option>
                        </select>
                      </div>

                      {manual && <>
                        <div>
                          <label className="mb-1 block text-xs font-semibold">{t('manualProductName')}</label>
                          <input className={inputClass} value={item.manualName} onChange={(event) => updateItem(index, 'manualName', event.target.value)} placeholder={t('productNamePlaceholder')} maxLength={120} required disabled={saving} />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold">{t('priceUnit')}</label>
                          <select className={inputClass} value={item.manualUnit} onChange={(event) => updateItem(index, 'manualUnit', event.target.value)} disabled={saving}>
                            <option value="KG">KG</option>
                            <option value="PIECE">{t('pieceUnit')}</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold">{t('unitPrice')}</label>
                          <input className={inputClass} type="number" min="0" step="0.01" value={item.manualPrice} onChange={(event) => updateItem(index, 'manualPrice', event.target.value)} placeholder="0.00" required disabled={saving} />
                        </div>
                      </>}

                      {unit === 'PIECE' ? (
                        <div>
                          <label className="mb-1 block text-xs font-semibold">{t('expectedPieces')}</label>
                          <input className={inputClass} type="number" min="1" step="1" inputMode="numeric" value={item.expectedQuantity} onChange={(event) => updateItem(index, 'expectedQuantity', event.target.value)} placeholder={t('quantityRequired')} required disabled={saving} />
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="mb-1 block text-xs font-semibold">{t('boxes')}</label>
                            <input className={inputClass} type="number" min="1" step="1" inputMode="numeric" value={item.expectedBoxes} onChange={(event) => updateItem(index, 'expectedBoxes', event.target.value)} placeholder={t('quantityOptional')} disabled={saving} />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold">{t('weightKg')}</label>
                            <input className={inputClass} type="number" min="0.01" step="0.01" inputMode="decimal" value={item.expectedWeight} onChange={(event) => updateItem(index, 'expectedWeight', event.target.value)} placeholder={item.expectedBoxes ? t('quantityOptional') : t('quantityRequired')} required={!item.expectedBoxes} disabled={saving} />
                          </div>
                        </>
                      )}

                      <div className="flex items-end justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800 sm:col-span-2">
                        <span>{t('snapshotUnitPrice')}: {Number.isFinite(unitPrice) ? `$${unitPrice.toFixed(2)} / ${unit === 'KG' ? 'KG' : t('pieceUnit')}` : '—'}</span>
                        <span className="font-bold">{t('estimatedLineTotal')}: {estimatedTotal === null ? t('pendingWeight') : `$${estimatedTotal.toFixed(2)}`}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('orderInventoryLinkPending')}</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold">{t('notesOptional')}</label>
            <textarea className={`${inputClass} min-h-20 resize-y`} value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={1000} placeholder={t('orderNotesPlaceholder')} disabled={saving} />
          </div>

          {error && <p role="alert" className="rounded-lg bg-red-100 p-3 text-sm text-red-900 dark:bg-red-900/40 dark:text-red-100">{error}</p>}

          <div className="flex flex-col-reverse justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row">
            <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-gray-300 px-4 py-2 font-semibold hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-800">{t('cancel')}</button>
            <button type="submit" disabled={saving || loadingData || loadingPrices} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 font-bold text-white hover:bg-blue-800 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-500">{saving ? t('saving') : t('saveOrder')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrderModal;
